import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { createListCollection } from "@ark-ui/react";
import { CircularProgress } from "@mui/material";

import type { ParameterTimeSeriesConfiguration, ParametersLocation } from "@/lib/api";
import type { TimeSeriesConfig } from "@/components/graph/graph.types";
import { TimeRangeDimensionValue } from "@/lib/dimensions/time-range-dimension";
import type { TimeSeriesParameterOption } from "@/features/shared/widgets/map/time-series/time-series-parameter.types";
import { makeTimeSeriesConfigs } from "@/features/shared/widgets/map/time-series/make-time-series-configs";
import { WidgetContextToolbar } from "@/features/shared/widget-context/widget-context-toolbar";
import { TimeRangeControl } from "@/features/shared/widget-context/controls/time-range-control";
import { RefreshControl } from "@/features/shared/widget-context/controls/refresh-control";
import Select from "@/components/select/select";
import Button from "@/components/button/button";
import { Icon } from "@/components/icon/icon";
import Popover from "@/components/popover/popover";
import Stack from "@/components/stack/stack";
import { useTranslation } from "@/lib/i18n";

import { resolveDefaultStationId } from "./resolve-default-station-id";
import styles from "./time-series-chart-preview.module.css";

/** Derived chart inputs handed to {@link RenderChart} so adapters stay stateless. */
export type RenderChartContext = {
  timeSeriesConfigs: TimeSeriesConfig[];
  currentTime: TimeRangeDimensionValue;
};

export type RenderChart = (
  station: ParametersLocation,
  context: RenderChartContext,
) => ReactNode;

type TimeSeriesChartPreviewProviderProps = {
  /** The single time-series group being edited (live draft), or `null` until valid. */
  config: ParameterTimeSeriesConfiguration | null;
  /** Catalog parameter options for the widget (drives labels/units/viz type). */
  parameters: TimeSeriesParameterOption[];
  /** `parameterId -> modelId`; absent keys are treated as observed (blue). */
  parameterModelIndex?: Record<string, string>;
  /** Fallback when `parameterModelIndex` is absent. Use `"model"` for model-only widgets. */
  defaultSeriesType?: "observed" | "model";
  /** Selectable stations (each carries `coordinates`); already filtered by the caller. */
  stations: ParametersLocation[];
  /** True while the station list is still loading. */
  stationsLoading: boolean;
  /** Renders the chart for the chosen station (observed data or a modeled sample). */
  renderChart: RenderChart;
  children: ReactNode;
};

type TimeSeriesChartPreviewValue = {
  config: ParameterTimeSeriesConfiguration | null;
  configuredParameterIds: string[];
  stations: ParametersLocation[];
  selectedStationId: string | undefined;
  setSelectedStationId: (value: string) => void;
  selectedStation: ParametersLocation | undefined;
  stationCollection: React.ComponentProps<typeof Select>["collection"];
  /** True once a valid configuration exists, so the toolbar should render. */
  showControls: boolean;
  stationsLoading: boolean;
  timeSeriesConfigs: TimeSeriesConfig[];
  currentTime: TimeRangeDimensionValue;
  renderChart: RenderChart;
};

const TimeSeriesChartPreviewContext = createContext<TimeSeriesChartPreviewValue | null>(
  null,
);

function useTimeSeriesChartPreview(): TimeSeriesChartPreviewValue {
  const value = useContext(TimeSeriesChartPreviewContext);
  if (!value) {
    throw new Error(
      "useTimeSeriesChartPreview must be used within a TimeSeriesChartPreviewProvider",
    );
  }
  return value;
}

/**
 * Shared chart-only preview scaffold for the time-series configurator dialog: it
 * renders the standard chart(s) for the single configuration currently being
 * edited, for one station chosen from a dropdown. The station list and the chart
 * renderer are supplied by the caller, so obs-map (observed data per station) and
 * model-map (model grid sampled at the station's lat/lng) share the same picker,
 * toolbar and body while differing only in those two adapters.
 *
 * The provider derives the station collection / selection and chart configs and
 * shares them with {@link TimeSeriesChartPreviewToolbar} (rendered in a dialog or
 * column header) and {@link TimeSeriesChartPreviewBody} (the chart). Splitting
 * toolbar from body lets the header own the controls while both halves read one
 * selection - the caller must wrap both in the same `WidgetContextHost` (and this
 * provider) so the charts get the runtime time range/timezone.
 */
export function TimeSeriesChartPreviewProvider({
  config,
  parameters,
  parameterModelIndex,
  defaultSeriesType,
  stations,
  stationsLoading,
  renderChart,
  children,
}: TimeSeriesChartPreviewProviderProps) {
  const configuredParameterIds = useMemo(
    () => config?.series.map((series) => series.parameter_id) ?? [],
    [config],
  );

  const [selectedStationId, setSelectedStationId] = useState<string | undefined>();

  // Default to the first station; keep the current one if still valid.
  useEffect(() => {
    setSelectedStationId((current) => resolveDefaultStationId(stations, current));
  }, [stations]);

  const selectedStation = stations.find((station) => station.id === selectedStationId);

  const stationCollection = useMemo(
    () =>
      createListCollection({
        items: stations.map((station) => ({
          label: station.name,
          value: station.id,
        })),
      }),
    [stations],
  );

  const timeSeriesConfigs = useMemo<TimeSeriesConfig[]>(
    () =>
      config
        ? makeTimeSeriesConfigs([config], parameters, {
            parameterModelIndex,
            defaultSeriesType,
          })
        : [],
    [config, parameters, parameterModelIndex, defaultSeriesType],
  );

  // No map player here, so pin the "current time" line to now (the position the
  // dashboard player starts at, within the seeded default range).
  const currentTime = useMemo(() => new TimeRangeDimensionValue(new Date()), []);

  const value: TimeSeriesChartPreviewValue = {
    config,
    configuredParameterIds,
    stations,
    selectedStationId,
    setSelectedStationId,
    selectedStation,
    stationCollection,
    // Once we intend to show a chart, keep the controls mounted (disabled while
    // their data loads) so the toolbar does not pop in and shift the layout.
    showControls: !!config && configuredParameterIds.length > 0,
    stationsLoading,
    timeSeriesConfigs,
    currentTime,
    renderChart,
  };

  return (
    <TimeSeriesChartPreviewContext.Provider value={value}>
      {children}
    </TimeSeriesChartPreviewContext.Provider>
  );
}

/**
 * Toolbar half of the chart preview: the station picker plus the shared
 * time-range / refresh controls. Renders nothing until a valid configuration
 * exists. Meant to be dropped into a dialog or preview-column header.
 */
export function TimeSeriesChartPreviewToolbar() {
  const {
    showControls,
    stationCollection,
    selectedStationId,
    setSelectedStationId,
    stations,
    selectedStation,
    stationsLoading,
  } = useTimeSeriesChartPreview();

  if (!showControls) return null;

  return (
    <WidgetContextToolbar className={styles.controlsBar}>
      <LocationControl
        collection={stationCollection}
        value={selectedStationId}
        onValueChange={setSelectedStationId}
        disabled={stationsLoading || stations.length === 0}
        selectedName={selectedStation?.name}
        loading={stationsLoading}
      />
      <TimeRangeControl />
      <RefreshControl />
    </WidgetContextToolbar>
  );
}

/** Chart half of the chart preview: hint / spinner / the caller's chart. */
export function TimeSeriesChartPreviewBody() {
  const { t } = useTranslation();
  const {
    config,
    configuredParameterIds,
    stationsLoading,
    selectedStation,
    renderChart,
    timeSeriesConfigs,
    currentTime,
  } = useTimeSeriesChartPreview();

  let body: ReactNode;
  if (!config || configuredParameterIds.length === 0) {
    body = (
      <p className={styles.hint}>
        {t("admin.widgets.fields.map_editor.time_series_chart_preview.configure_hint")}
      </p>
    );
  } else if (stationsLoading) {
    body = (
      <Stack alignItems="center" justifyContent="center">
        <CircularProgress size={24} color="inherit" />
      </Stack>
    );
  } else if (!selectedStation) {
    body = (
      <p className={styles.hint}>
        {t("admin.widgets.fields.map_editor.time_series_chart_preview.no_stations")}
      </p>
    );
  } else {
    body = renderChart(selectedStation, { timeSeriesConfigs, currentTime });
  }

  return <div className={styles.body}>{body}</div>;
}

type LocationControlProps = {
  collection: React.ComponentProps<typeof Select>["collection"];
  value: string | undefined;
  onValueChange: (value: string) => void;
  disabled: boolean;
  selectedName: string | undefined;
  loading: boolean;
};

/**
 * Station picker for the chart-preview toolbar, modeled after the dashboard
 * location tool: a compact trigger button (shows the selected station) opens a
 * popover holding the labeled select, so the toolbar row stays short.
 */
function LocationControl({
  collection,
  value,
  onValueChange,
  disabled,
  selectedName,
  loading,
}: LocationControlProps) {
  const { t } = useTranslation();
  const label = t(
    "admin.widgets.fields.map_editor.time_series_chart_preview.station_label",
  );
  const placeholder = loading
    ? t("admin.widgets.fields.map_editor.time_series_chart_preview.loading_stations")
    : label;

  return (
    <Popover
      nested
      trigger={(context) => (
        <Button variant="primary" disabled={disabled}>
          <Icon name="map-pin" size={16} />
          <span>{selectedName ?? label}</span>
          <Icon name={context.open ? "chevron-up" : "chevron-down"} size={16} />
        </Button>
      )}
    >
      <div className={styles.locationPanel}>
        <Select
          collection={collection}
          value={value}
          onValueChange={onValueChange}
          disabled={disabled}
          label={label}
          placeholder={placeholder}
        />
      </div>
    </Popover>
  );
}
